import { useState } from 'react';
import { Modal, Image, Text, Button, ActionIcon, Box, Group, Badge } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { useStore } from '@/store';
import type { ApiItem, ApiItemDetail } from '@/models/Item';
import { formatKNumber, translate } from '@/lib';
import { RarityBadge } from '@/components/RarityBadge';
import { getRareInfo } from '@/models/Metadata';
import style from './Style.module.scss';

type CompareItem = ApiItem | ApiItemDetail;

interface CompareModalProps {
  onSelectItem?: (index: number) => void;
}

export function CompareModal({ onSelectItem: _ }: CompareModalProps) {
  const { compareList, compareModalOpen, setCompareModalOpen, removeFromCompare, clearCompareList } = useStore();
  const [_selectingIndex, setSelectingIndex] = useState<number | null>(null);

  // Ensure we always have 3 slots (fill with null for empty slots)
  const slots: (CompareItem | null)[] = [...compareList, null, null, null].slice(0, 3);

  // Format a single stat value with color coding
  const formatStatValue = (value: number | undefined) => {
    if (value === undefined) return <Text component="span" size="xs" c="dimmed">~</Text>;
    const color = value > 0 ? 'green' : value < 0 ? 'red' : undefined;
    const prefix = value > 0 ? '+' : '';
    return (
      <Text component="span" size="xs" c={color} fw={600}>
        {prefix}{value}
      </Text>
    );
  };

  // Get stat value from item
  const getStatValue = (item: CompareItem | null, statKey: keyof NonNullable<CompareItem['stats']>) => {
    if (!item) return <Text component="span" size="xs" c="dimmed">-</Text>;
    if (!item?.stats?.[statKey]) return <Text component="span" size="xs" c="dimmed">~</Text>;
    return formatStatValue(item?.stats?.[statKey] as number);
  };

  const handleClose = () => {
    setCompareModalOpen(false);
    setSelectingIndex(null);
  };

  const handleRemove = (itemId: number) => {
    removeFromCompare(itemId);
  };

  // Get rarity badge for an item
  const getRarityBadge = (item: CompareItem | null) => {
    if (!item) return '-';
    const rareInfo = getRareInfo(item.rare);
    return <RarityBadge rarity={rareInfo.name as any} rare={item.rare} size="xs" />;
  };

  // Define stat rows for comparison
  const statRows: { label: string; statKey: keyof NonNullable<CompareItem['stats']> }[] = [
    { label: translate('ff.item.stats.damage', 'Damage'), statKey: 'damage' },
    { label: translate('ff.item.stats.accuracy', 'Accuracy'), statKey: 'accuracy' },
    { label: translate('ff.item.stats.range', 'Range'), statKey: 'range' },
    { label: translate('ff.item.stats.speed', 'Rate of Fire'), statKey: 'speed' },
    { label: translate('ff.item.stats.reload', 'Reload Speed'), statKey: 'reload' },
    { label: translate('ff.item.stats.cartridge', 'Magazine'), statKey: 'cartridge' },
    { label: translate('ff.item.stats.biteArmor', 'Armor Pen'), statKey: 'biteArmor' },
    { label: translate('ff.item.stats.playerSpeedFactor', 'Movement'), statKey: 'playerSpeedFactor' },
  ];

  const rows = [
    { label: translate('ff.item.name'), getValue: (item: CompareItem | null) => item?.translate || item?.name || '-' },
    { label: translate('ff.item.tier'), getValue: (item: CompareItem | null) => getRarityBadge(item) },
    // Individual stat rows
    ...statRows.map(({ label, statKey }) => ({
      label: label.toUpperCase(),
      getValue: (item: CompareItem | null) => getStatValue(item, statKey),
    })),
    { 
      label: translate('ff.item.wishlist'), 
      getValue: (item: CompareItem | null) => item?.wishlist 
        ? formatKNumber(item.wishlist)
        : '-' 
    },
    { 
      label: translate('ff.item.likes'), 
      getValue: (item: CompareItem | null) => item?.like 
        ? formatKNumber(item.like)
        : '-' 
    },
  ];

  return (
    <Modal
      opened={compareModalOpen}
      onClose={handleClose}
      size="xl"
      withCloseButton={false}
      classNames={{ body: style.modalBody }}
      centered
    >
      <Box className={style.container}>
        <ActionIcon
          className={style.closeButton}
          variant="subtle"
          color="gray"
          onClick={handleClose}
        >
          <IconX size={20} />
        </ActionIcon>

        {/* Comparison table - same layout for desktop and mobile; mobile scrolls horizontally with sticky label column */}
        <div className={style.tableScrollWrapper}>
          <table className={style.comparisonTable}>
            <tbody>
              {/* Images Row */}
              <tr>
                <td className={style.labelCell}></td>
                <td className={style.separator}></td>
                {slots.map((item, index) => (
                  <td key={index} className={style.imageCell}>
                    <Box pos="relative" style={{ display: 'inline-block' }}>
                      {item ? (
                        <div className={`${style.imageContainer} tier-bg-${item.rare || 1}`}>
                          <Image
                            src={item.icon}
                            alt={item.name}
                            width={80}
                            height={80}
                            fit="contain"
                            radius="sm"
                          />
                          {item.isOwned && (
                            <Badge className={style.ownedBadge} size="xs" variant="filled" color="teal" radius="sm">
                              {translate('ff.item.owned', 'Owned')}
                            </Badge>
                          )}
                          <ActionIcon
                            className={style.removeButton}
                            variant="filled"
                            color="red"
                            size="sm"
                            radius="xl"
                            onClick={() => handleRemove(item.id)}
                          >
                            <IconX size={14} />
                          </ActionIcon>
                        </div>
                      ) : (
                        <div className={style.emptySlot}>
                          {/* Empty slot placeholder */}
                        </div>
                      )}
                    </Box>
                  </td>
                ))}
              </tr>
              {/* Stats Rows */}
              {rows.map((row) => (
                <tr key={row.label}>
                  <td className={style.labelCell}>{row.label}</td>
                  <td className={style.separator}>:</td>
                  {slots.map((item, index) => (
                    <td key={index} className={style.valueCell}>
                      {row.getValue(item)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Group justify="center" mt="xl">
          <Button
            variant="subtle"
            color="gray"
            onClick={clearCompareList}
          >
            {translate('ff.compare.clearAll')}
          </Button>
        </Group>
      </Box>
    </Modal>
  );
}
